import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import EmptyState from '../../../components/feedback/EmptyState';
import StatusBadge from '../../../components/ui/StatusBadge';
import type { TariffListTableProps } from '../types';

const TariffListTable = ({
  tariffs,
  canManage,
  onEdit,
  onDelete,
  onCreate,
  formatCurrency,
  formatDate,
  isValidityActive,
  t,
}: TariffListTableProps) => {
  if (tariffs.length === 0) {
    return (
      <EmptyState
        title={t('warehouses.tariffs.noTariffs') || 'No tariffs defined yet.'}
        message={t('warehouses.tariffs.noTariffsHelp', 'Create the first tariff to manage pricing and validity windows for this item.')}
        action={canManage && onCreate ? (
          <Button variant="primary" onClick={onCreate}>
            <i className="ti ti-plus me-2"></i>
            {t('warehouses.tariffs.addNew') || 'Add Tariff'}
          </Button>
        ) : null}
      />
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-vcenter mb-0 app-table">
        <thead>
          <tr>
            <th>{t('warehouses.tariffs.name') || 'Name'}</th>
            <th>{t('warehouses.tariffs.type') || 'Type'}</th>
            <th>{t('warehouses.tariffs.amount') || 'Amount'}</th>
            <th>{t('warehouses.tariffs.validFrom') || 'Valid From'}</th>
            <th>{t('warehouses.tariffs.validTo') || 'Valid To'}</th>
            <th>{t('warehouses.tariffs.priority') || 'Priority'}</th>
            <th>{t('warehouses.tariffs.status') || 'Status'}</th>
            <th className="text-center">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {tariffs.map((tariff) => {
            const isActive = tariff.is_active && isValidityActive(tariff.valid_from, tariff.valid_to);

            return (
              <tr key={tariff.id}>
                <td className="fw-medium">{tariff.name || '-'}</td>
                <td>
                  <StatusBadge color={tariff.tariff_type?.color} label={tariff.tariff_type?.name || '-'} />
                  {tariff.is_promotional && (
                    <Badge bg="danger" className="ms-1" style={{ color: '#ffffff', fontWeight: '500' }}>
                      {t('warehouses.tariffs.promotional') || 'Promo'}
                    </Badge>
                  )}
                </td>
                <td className="fw-medium">
                  {formatCurrency(tariff.amount)}
                  {tariff.amount_vat && (
                    <small className="text-muted d-block">
                      {t('warehouses.tariffs.withVat') || 'With VAT'}: {formatCurrency(tariff.amount_vat)}
                    </small>
                  )}
                </td>
                <td>{formatDate(tariff.valid_from)}</td>
                <td>{formatDate(tariff.valid_to)}</td>
                <td>
                  <Badge bg="secondary" style={{ color: '#ffffff', fontWeight: '500' }}>{tariff.priority}</Badge>
                </td>
                <td>
                  {isActive ? (
                    <Badge bg="success" style={{ color: '#ffffff', fontWeight: '500' }}>
                      <i className="ti ti-check me-1"></i>
                      {t('warehouses.tariffs.active') || 'Active'}
                    </Badge>
                  ) : (
                    <Badge bg="secondary" style={{ color: '#ffffff', fontWeight: '500' }}>
                      <i className="ti ti-x me-1"></i>
                      {t('warehouses.tariffs.inactive') || 'Inactive'}
                    </Badge>
                  )}
                </td>
                <td className="text-center">
                  <div className="btn-group">
                    {canManage && (
                      <>
                        <Button size="sm" variant="outline-primary" onClick={() => onEdit(tariff)} title={t('common.edit')}>
                          <i className="ti ti-edit"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(tariff.id)} title={t('common.delete')}>
                          <i className="ti ti-trash"></i>
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TariffListTable;
